import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      maxWidth: "90vw",
      width: "90vw",
      maxHeight: "90vh",
    },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
  },
  mainImageContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
    backgroundColor: "#f5f5f5",
    borderRadius: theme.spacing(1),
    overflow: "hidden",
  },
  mainImage: {
    maxWidth: "100%",
    maxHeight: "60vh",
    objectFit: "contain",
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
    },
    zIndex: 1,
  },
  navButtonLeft: {
    left: theme.spacing(1),
  },
  navButtonRight: {
    right: theme.spacing(1),
  },
  thumbnailContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      height: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 3,
    },
  },
  thumbnailItem: {
    cursor: "pointer",
    border: "2px solid transparent",
    borderRadius: theme.spacing(1),
    overflow: "hidden",
    transition: "border-color 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.light,
    },
  },
  thumbnailActive: {
    borderColor: theme.palette.primary.main,
  },
  thumbnailImage: {
    width: 80,
    height: 60,
    objectFit: "cover",
    display: "block",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 300,
    gap: theme.spacing(2),
  },
  counterText: {
    position: "absolute",
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "#fff",
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.5),
    fontSize: "0.85rem",
  },
}));

const PictureCarousel = ({
  open,
  visitId,
  onClose,
  title,
  fetchPicturesFn,
}) => {
  const classes = useStyles();
  const [pictures, setPictures] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPictures = useCallback(async () => {
    if (!visitId) return;
    setLoading(true);
    setError(null);
    setPictures([]);
    setCurrentIndex(0);
    try {
      const response = await fetchPicturesFn(visitId);
      const data = response?.data;
      if (data?.succeeded && data?.data) {
        setPictures(data.data);
      } else {
        setError(data?.message || "Failed to load pictures");
        toast.error(data?.message || "Failed to load pictures");
      }
    } catch (err) {
      const errorMsg =
        typeof err === "string"
          ? err
          : "Something went wrong while loading pictures";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [visitId, fetchPicturesFn]);

  useEffect(() => {
    if (open && visitId) {
      fetchPictures();
    }
    if (!open) {
      setPictures([]);
      setCurrentIndex(0);
      setError(null);
      setLoading(false);
    }
  }, [open, visitId, fetchPictures]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : pictures.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < pictures.length - 1 ? prev + 1 : 0));
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const handleKeyDown = (event) => {
    if (!open) return;
    if (event.key === "ArrowLeft") handlePrevious();
    if (event.key === "ArrowRight") handleNext();
    if (event.key === "Escape") onClose();
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const getImageSrc = (picture) => {
    if (!picture?.base64ImageData || !picture?.contentType) return "";
    return `data:${picture.contentType};base64,${picture.base64ImageData}`;
  };

  const renderMainContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="textSecondary">
            Loading pictures...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box className={classes.errorContainer}>
          <PhotoLibraryIcon sx={{ fontSize: 60 }} color="disabled" />
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            No pictures are available for this visit.
          </Typography>
        </Box>
      );
    }

    if (pictures.length === 0) {
      return (
        <Box className={classes.errorContainer}>
          <PhotoLibraryIcon sx={{ fontSize: 60 }} color="disabled" />
          <Typography variant="h6" color="textSecondary">
            No Pictures Found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This visit has no attached pictures.
          </Typography>
        </Box>
      );
    }

    const currentPicture = pictures[currentIndex];
    const imageSrc = getImageSrc(currentPicture);

    return (
      <Box className={classes.mainImageContainer}>
        {pictures.length > 1 && (
          <IconButton
            className={`${classes.navButton} ${classes.navButtonLeft}`}
            onClick={handlePrevious}
            size="large"
            aria-label="Previous image"
          >
            <NavigateBeforeIcon fontSize="large" />
          </IconButton>
        )}

        <img
          className={classes.mainImage}
          src={imageSrc}
          alt={currentPicture?.fileName || `Picture ${currentIndex + 1}`}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.nextSibling.style.display = "flex";
          }}
        />
        <Box
          display="none"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
          flexDirection="column"
          gap={1}
        >
          <PhotoLibraryIcon sx={{ fontSize: 40 }} color="disabled" />
          <Typography variant="body2" color="textSecondary">
            Failed to display image
          </Typography>
        </Box>

        {pictures.length > 1 && (
          <IconButton
            className={`${classes.navButton} ${classes.navButtonRight}`}
            onClick={handleNext}
            size="large"
            aria-label="Next image"
          >
            <NavigateNextIcon fontSize="large" />
          </IconButton>
        )}

        {pictures.length > 1 && (
          <Typography className={classes.counterText}>
            {currentIndex + 1} / {pictures.length}
          </Typography>
        )}
      </Box>
    );
  };

  const renderThumbnails = () => {
    if (loading || error || pictures.length <= 1) return null;

    return (
      <Box className={classes.thumbnailContainer}>
        <ImageList
          cols={pictures.length > 6 ? 6 : pictures.length}
          rowHeight={60}
          gap={4}
          sx={{ m: 0, width: "auto" }}
        >
          {pictures.map((picture, index) => (
            <ImageListItem
              key={picture.pictureId || index}
              className={`${classes.thumbnailItem} ${
                index === currentIndex ? classes.thumbnailActive : ""
              }`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img
                className={classes.thumbnailImage}
                src={getImageSrc(picture)}
                alt={picture?.fileName || `Thumbnail ${index + 1}`}
                loading="lazy"
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          color="inherit"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {renderMainContent()}
        {renderThumbnails()}
      </DialogContent>
    </Dialog>
  );
};

export default PictureCarousel;
